import { InstitutionalServiceContext } from '../../institution-history/traffic-history.models';
import type {
  InstitutionContext,
  MonthlyReportIncident,
  MonthlyReportPeriodStatus,
  PanelError,
} from '../monthly-report.models';
import {
  currentLimaMonth,
  formatLimaDateTime,
  formatLimaMonthLabel,
  roundOrNull,
  shiftYearMonth,
} from '../../institution-history/utils/traffic-format.util';

const YEAR_MONTH_RE = /^\d{4}-\d{2}$/;
const MONTH_LOOKBACK = 24;

export const INCOMPLETE_AVAILABILITY_HINT =
  'La disponibilidad aún no puede calcularse porque existen incidencias del período pendientes de revisión.';

export const AVAILABILITY_SOURCE_NOTE =
  'La disponibilidad se determina a partir de las incidencias del servicio registradas durante el período.';

export const TRAFFIC_NO_DATA_NOTE = 'Sin datos de tráfico para este período.';

export const TRAFFIC_UNAVAILABLE_NOTE =
  'Parte de la información de tráfico del período no está disponible.';

export const EMPTY_SERVICES_MESSAGE =
  'No hay un servicio institucional contratado disponible para consultar el reporte.';

export const MANY_SERVICES_MESSAGE =
  'No se pudo determinar el servicio institucional asociado. Comuníquese con soporte.';

export const PDF_CHART_EXPORT_ERROR =
  'No se pudo generar el gráfico del reporte. Inténtalo de nuevo.';

const LIMA_TIMEZONE = 'America/Lima';
const PDF_SERVICE_NAME = 'Internet Dedicado';

const CATEGORY_LABELS: Record<string, string> = {
  NO_SERVICE: 'Sin servicio',
  SLOW_SPEED: 'Internet lento',
  INTERMITTENT: 'Intermitencia',
  INSTALLATION: 'Instalación',
  BILLING: 'Pagos',
  OTHER: 'Otros',
};

const STATUS_LABELS: Record<string, string> = {
  OPEN: 'Abierto',
  ASSIGNED: 'Asignado',
  IN_PROGRESS: 'En Progreso',
  PENDING_CUSTOMER: 'Pend. Cliente',
  RESOLVED: 'Resuelto',
  CLOSED: 'Cerrado',
  CANCELLED: 'Cancelado',
};

export interface MonthOption {
  value: string;
  label: string;
}

export type InstitutionalServiceResolution =
  | { kind: 'empty' }
  | { kind: 'many'; count: number }
  | { kind: 'one'; service: InstitutionalServiceContext };

export function resolveInstitutionalService(
  services: InstitutionalServiceContext[] | null | undefined,
): InstitutionalServiceResolution {
  const list = (Array.isArray(services) ? services : []).filter((item) => {
    const id = Number(item?.clientServiceId);
    return Number.isInteger(id) && id > 0;
  });

  if (list.length === 0) return { kind: 'empty' };
  if (list.length > 1) return { kind: 'many', count: list.length };
  return { kind: 'one', service: list[0] };
}

/** Último mes cerrado en America/Lima (el mes actual sigue PRELIMINARY). */
export function lastClosedLimaMonth(nowMonth: string = currentLimaMonth()): string {
  return shiftYearMonth(nowMonth, -1);
}

export function isValidYearMonth(value: string | null | undefined): value is string {
  if (!value || !YEAR_MONTH_RE.test(value)) return false;
  const month = Number(value.slice(5, 7));
  return month >= 1 && month <= 12;
}

/**
 * Query `yearMonth` usable: válido, calendario real y no futuro.
 * Si no aplica, cae al último mes cerrado.
 */
export function resolveYearMonthParam(
  raw: string | null | undefined,
  currentMonth: string = currentLimaMonth(),
): string {
  if (!isValidYearMonth(raw) || raw > currentMonth) {
    return lastClosedLimaMonth(currentMonth);
  }
  return raw;
}

export function buildMonthOptions(
  currentMonth: string = currentLimaMonth(),
  lookback: number = MONTH_LOOKBACK,
): MonthOption[] {
  const options: MonthOption[] = [];
  for (let i = 0; i < lookback; i++) {
    const value = shiftYearMonth(currentMonth, -i);
    options.push({ value, label: formatLimaMonthLabel(value) });
  }
  return options;
}

export function ensureMonthOption(options: MonthOption[], yearMonth: string): MonthOption[] {
  if (options.some((option) => option.value === yearMonth)) return options;
  return [...options, { value: yearMonth, label: formatLimaMonthLabel(yearMonth) }].sort((a, b) =>
    b.value.localeCompare(a.value),
  );
}

export function formatBytesSi(bytes: number | null | undefined): string | null {
  if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes)) || bytes < 0) {
    return null;
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Number(bytes);
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  const decimals = unit === 0 ? 0 : value >= 100 ? 0 : value >= 10 ? 1 : 2;
  const rounded = roundOrNull(value, decimals);
  if (rounded === null) return null;
  return `${rounded.toLocaleString('es-PE', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  })} ${units[unit]}`;
}

export function formatDurationSeconds(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds)) || seconds < 0) {
    return null;
  }
  const total = Math.floor(Number(seconds));
  if (total === 0) return '0 min';
  if (total < 60) return '< 1 min';

  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const parts: string[] = [];
  if (days) parts.push(`${days} d`);
  if (hours) parts.push(`${hours} h`);
  if (minutes || parts.length === 0) parts.push(`${minutes} min`);
  return parts.join(' ');
}

export function formatMbps(value: number | null | undefined): string {
  const rounded = roundOrNull(value ?? null, 2);
  if (rounded === null) return '—';
  return `${rounded.toFixed(2)} Mbps`;
}

export function formatPercent(value: number | null | undefined): string | null {
  const rounded = roundOrNull(value ?? null, 2);
  if (rounded === null) return null;
  return `${rounded.toFixed(2)} %`;
}

export function ticketCategoryLabel(category: string | null | undefined): string {
  if (!category) return '—';
  return CATEGORY_LABELS[category] ?? category;
}

export function ticketStatusLabel(status: string | null | undefined): string {
  if (!status) return '—';
  return STATUS_LABELS[status] ?? status;
}

export function formatIncidentCode(code: string | null | undefined): string {
  const value = code?.trim();
  return value ? value : '—';
}

export function formatIncidentPeriod(incident: MonthlyReportIncident): string {
  const start = formatLimaDateTime(incident.createdAt);
  const endIso = incident.resolvedAt || incident.closedAt;
  if (!endIso) return `${start} → Abierto`;
  return `${start} → ${formatLimaDateTime(endIso)}`;
}

export function formatUgelContext(ugel: string | null | undefined): string {
  const value = ugel?.trim();
  if (!value) return '';
  return /^ugel\b/i.test(value) ? value : `UGEL ${value}`;
}

export function formatHeaderSubtitle(header: InstitutionContext | null | undefined): string {
  if (!header) return '';
  const name = header.institutionName?.trim() || '';
  const code = header.modularCode?.trim() || '';
  const ugel = formatUgelContext(header.ugel);
  const parts: string[] = [];
  if (name) {
    parts.push(name);
    if (code) parts.push(code);
  } else if (code) {
    parts.push(code);
  } else {
    parts.push('—');
  }
  if (ugel) parts.push(ugel);
  return parts.join(' · ');
}

export function formatLocality(header: InstitutionContext | null | undefined): string {
  const locality = header?.locality;
  if (!locality) return '—';
  const parts: string[] = [];
  const district = locality.district?.trim();
  const province = locality.province?.trim();
  const department = locality.department?.trim();
  if (district) parts.push(district);
  if (province && province !== district) parts.push(province);
  if (department && department !== province && department !== district) parts.push(department);
  return parts.join(' · ') || '—';
}

export function formatContractedPlan(header: InstitutionContext | null | undefined): string {
  const plan = header?.contractedPlan;
  if (!plan) return '—';
  const down = plan.downloadMbps ?? '—';
  const up = plan.uploadMbps ?? '—';
  return `${down}/${up} Mbps`;
}

export function formatPdfContractedPlan(header: InstitutionContext | null | undefined): string {
  const speeds = formatContractedPlan(header);
  if (speeds === '—') return '—';
  return `${PDF_SERVICE_NAME} ${speeds}`;
}

export function canDownloadMonthlyReportPdf(
  periodStatus: MonthlyReportPeriodStatus | null | undefined,
  downloadAllowed: boolean | null | undefined,
): boolean {
  return periodStatus === 'CLOSED' && downloadAllowed === true;
}

export function pdfDownloadTooltip(
  periodStatus: MonthlyReportPeriodStatus | null | undefined,
  downloadAllowed: boolean | null | undefined,
): string {
  if (!canDownloadMonthlyReportPdf(periodStatus, downloadAllowed)) {
    return 'El PDF se habilita cuando el mes esté cerrado';
  }
  return 'Descargar reporte en PDF';
}

export function buildMonthlyReportPdfFilename(
  modularCode: string | null | undefined,
  serviceId: number,
  yearMonth: string,
): string {
  const code = modularCode?.trim();
  const idPart = code || String(serviceId);
  return `Reporte_Conectividad_IE_${idPart}_${yearMonth}.pdf`;
}

export function formatPdfIncidentPeriod(
  incident: MonthlyReportIncident,
  monthStartIso: string,
  monthEndIso: string,
): string {
  const monthStart = Date.parse(monthStartIso);
  const monthEnd = Date.parse(monthEndIso);
  const created = Date.parse(incident.createdAt);
  if (Number.isNaN(monthStart) || Number.isNaN(monthEnd) || Number.isNaN(created)) {
    return '—';
  }

  const startMs = Math.max(created, monthStart);
  const startLabel = formatPdfCompactDateTime(startMs);
  const endIso = incident.resolvedAt || incident.closedAt;
  const resolved = endIso ? Date.parse(endIso) : monthEnd;
  const clippedEnd = Number.isNaN(resolved) ? monthEnd : Math.min(resolved, monthEnd);

  return `${startLabel}–${formatPdfCompactDateTime(clippedEnd)}`;
}

function formatPdfCompactDateTime(ms: number): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LIMA_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${value('day')}/${value('month')} ${value('hour')}:${value('minute')}`;
}

export function toMonthlyReportError(err: {
  status?: number | null;
  error?: { error?: { code?: string | null } } | null;
}): PanelError {
  const status = err?.status ?? null;
  const apiCode = err?.error?.error?.code ?? null;

  if (status === 403) {
    return {
      httpStatus: 403,
      code: apiCode,
      message: 'No tienes acceso al reporte mensual de esta institución.',
      retryable: false,
    };
  }
  if (status === 404) {
    return {
      httpStatus: 404,
      code: apiCode,
      message: 'No se encontró el servicio institucional solicitado.',
      retryable: false,
    };
  }
  if (status === 422) {
    return {
      httpStatus: 422,
      code: apiCode,
      message: 'El mes seleccionado no es válido.',
      retryable: false,
    };
  }
  if (status === 502 || status === 503) {
    return {
      httpStatus: status,
      code: apiCode,
      message: 'El reporte mensual no está disponible en este momento.',
      retryable: true,
    };
  }

  return {
    httpStatus: status,
    code: apiCode,
    message: 'Ocurrió un error al cargar el reporte mensual.',
    retryable: true,
  };
}

export function toInstitutionalServicesError(err: {
  status?: number | null;
  error?: { error?: { code?: string | null } } | null;
}): PanelError {
  const status = err?.status ?? null;
  const apiCode = err?.error?.error?.code ?? null;

  if (status === 403) {
    return {
      httpStatus: 403,
      code: apiCode,
      message: 'No tienes acceso a los servicios institucionales.',
      retryable: false,
    };
  }

  return {
    httpStatus: status,
    code: apiCode,
    message: 'Ocurrió un error al identificar el servicio institucional.',
    retryable: true,
  };
}
